import * as mobx from 'mobx';
import { toJS } from 'mobx';

import { createContext, useContext } from 'react';

import { api as axiosApi } from './axios';
// import { storage } from './storage';

export const ProjectContext = createContext({});
export const useProject = () => useContext(ProjectContext);

class Project {
  id = '';
  name = '';
  user = {};
  skipSaving = false;
  cloudEnabled = true;
  status = 'saved'; // can be 'saved', 'has-changes', 'saving'
  saveButtonStatus = 'default'; // can be 'default', 'saving', 'saved'
  language = navigator.language || 'en';
  designsLength = 0;
  lastSaveTime = 0;
  document = null;

  constructor({ store }) {
    mobx.makeAutoObservable(this);
    this.store = store;

    store.on('change', () => {
      this.requestSave();
    });

    // Add keyboard shortcut for save
    window.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        this.forceSave();
      }
    });
  }

  async firstLoad() {
    // Get document ID from URL
    const urlParams = new URLSearchParams(window.location.search);
    this.id = urlParams.get('id') || '';
    
    if (this.id) {
      await this.loadById(this.id);
    } else {
      await this.createNewDesign();
    }
  }

  requestSave() {
    this.status = 'has-changes';
    if (this.saveTimeout) {
      return;
    }
    
    const now = Date.now();
    const timeSinceLastSave = now - this.lastSaveTime;
    const delay = Math.max(5000 - timeSinceLastSave, 0);

    this.saveTimeout = setTimeout(() => {
      this.saveTimeout = null;
      this.save();
    }, delay);
  }

  async forceSave() {
    if (this.status === 'saving') return;
    
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    
    await this.save();
  }

  async loadById(id) {
    this.id = id;
    this.status = 'loading';
    try {
      const response = await axiosApi.get(`/document/${id}`);
      if (response.ok && response.data) {
        // Convert the response data to a plain JavaScript object
        const plainDocument = toJS(response.data);
        this.document = plainDocument;
        
        if (plainDocument.content) {
          // Load a plain JavaScript copy of the content into the store
          this.store.loadJSON(toJS(plainDocument.content));
        }
        this.name = plainDocument.name || 'Untitled Design';
        
        // Notify parent that load is complete
        const payload = {
          action: 'loaded'
        };
        window.parent.postMessage(payload, process.env.NODE_ENV === 'development' ? 'http://localhost:8081' : 'https://app.taffytree.com');
      }
    } catch (e) {
      console.error('Error loading document:', e);
      this.name = 'Untitled Design';
      this.document = null;
    }
    this.status = 'saved';
    this.saveButtonStatus = 'default';
  }

  async save() {
    if (!this.id || !this.document) return;
    
    this.status = 'saving';
    this.saveButtonStatus = 'saving';
    
    try {
      // Create a plain JavaScript copy of the document before updating
      const plainDocument = toJS(this.document);
      const updatedDocument = {
        ...plainDocument,
        content: this.store.toJSON(),
        name: this.name
      };

      const response = await axiosApi.put(`/document/${this.id}`, updatedDocument);
      
      if (response.ok) {
        this.document = updatedDocument;
        this.lastSaveTime = Date.now();
        this.status = 'saved';
        this.saveButtonStatus = 'saved';
        
        // Reset save button status after 2 seconds
        setTimeout(() => {
          this.saveButtonStatus = 'default';
        }, 2000);
      }
    } catch (e) {
      console.error('Error saving document:', e);
      this.status = 'has-changes';
      this.saveButtonStatus = 'default';
      
      const payload = {
        action: 'error',
        message: 'Failed to save document'
      };
      window.parent.postMessage(payload, process.env.NODE_ENV === 'development' ? 'http://localhost:8081' : 'https://app.taffytree.com');
    }
  }

  async clear() {
    this.store.clear();
    this.store.addPage();
    this.document = null;
  }

  async createNewDesign() {
    await this.clear();
    this.name = 'Untitled Design';
    this.id = '';
    await this.save();
  }
}

export const createProject = (...args) => new Project(...args);
export default createProject;
